// frontend/src/native/liveCall.ts
//
// Hintergrund-Call-Modus fürs Live-Gespräch — dünne Brücke zum nativen
// 'LiveCall'-Plugin (Android: Foreground-Service + Call-Notification mit
// Beenden-Knopf; iOS: Hintergrund-Audio-Session). Auf Web/PWA und in
// Alt-Shells OHNE das Plugin ist ALLES hier ein No-Op — das Live-Gespräch
// darf am Bridge-Ausfall nie scheitern (der Web-Audio-/Wake-Lock-Pfad im
// Hook trägt die Session dann allein). Fehler werden deshalb IMMER
// geschluckt und nur per console.warn gemeldet.
//
// STATISCHE Imports nach dem ota.ts-Muster (OTA-Lehre Builds 70035/70037):
// lazy `import()` von Plugin-Chunks hing unter capacitor:// im Boot →
// komplett vermieden. registerPlugin holt nur den nativen Proxy, es wird
// KEIN JS-Plugin-Modul geladen; NIE String-Variablen-Imports.
//
// F3/P5 (Hörbuch-Audiomodus 13.09.2026): start() akzeptiert `mode: 'call'`
// (Default, heutiger Sprachpfad) oder 'playback' (nur Wiedergabe ohne
// Sprachverarbeitung). setMode wechselt im laufenden Betrieb; updateNowPlaying
// fuellt Sperrbildschirm/Notification; Events fuer Remote-Commands und
// Audiofokus. Alle Erweiterungen sind abwaertskompatibel: alte Shells ohne
// die neuen Methoden bleiben No-Op (siehe hasNativeMethod).
import { Capacitor, registerPlugin } from '@capacitor/core';

// ─── Typen ───────────────────────────────────────────────────────────────────

/** Handle wie Capacitors PluginListenerHandle — remove() hängt den Listener ab. */
export interface LiveCallListenerHandle {
  remove: () => void;
}

/**
 * Audio-Modus des Plugins. 'call' = heutiger Sprachpfad (playAndRecord /
 * MODE_IN_COMMUNICATION), Mikrofon-Aufnahme + VoIP-Verarbeitung.
 * 'playback' = reine Wiedergabe (AVAudioSession .playback mit .spokenAudio
 * bzw. AudioFocusRequest USAGE_MEDIA), OHNE Sprachverarbeitung — das ist
 * der Pfad fuer das Hoerbuch (Mikrofon bleibt aus).
 */
export type NativeAudioMode = 'call' | 'playback';

export interface LiveCallStartOptions {
  /** Titel der Call-Notification (lokalisiert). */
  title: string;
  /** Untertitel/Text der Notification (lokalisiert). */
  text: string;
  /** Android-Notification-Channel-Name (lokalisiert, einmalig registriert). */
  channelName: string;
  /** Beschriftung des Beenden-Knopfs in der Notification (lokalisiert). */
  endLabel: string;
  /**
   * Audio-Modus: 'playback' = nur Wiedergabe ohne Sprachverarbeitung
   * (Hoerbuch), 'call' = Sprachpfad inkl. Mikrofon (Default = 'call',
   * bytegleich zu allen bisherigen Aufrufern).
   */
  mode?: NativeAudioMode;
}

/** Payload fuer updateNowPlaying — Sperrbildschirm/Medienbenachrichtigung. */
export interface NativeNowPlayingInfo {
  /** Kapitel- oder Abschnitts-Titel. */
  title: string;
  /** Optionaler Untertitel (z.B. Sprecher/Abschnitt). */
  subtitle?: string;
  /** Gesamtdauer in Millisekunden. */
  durationMs: number;
  /** Aktuelle Abspielposition in Millisekunden. */
  positionMs: number;
  /** true = Wiedergabe laeuft, false = pausiert. */
  playing: boolean;
}

/** Events des nativen Plugins. */
type LiveCallEventName =
  | 'endRequested'
  | 'interruptionEnded'
  | 'interruptionBegan'
  | 'remotePlay'
  | 'remotePause'
  | 'remoteToggle'
  | 'remoteNext'
  | 'remotePrevious'
  | 'remoteSeek'
  | 'audioFocusLost'
  | 'audioFocusGained';

/** Payload, die das Plugin an Listener-Callbacks uebergibt. */
export interface NativeListenerPayload {
  /** positionMs bei 'remoteSeek'; sonst undefiniert. */
  positionMs?: number;
  /** transient-Flag bei 'audioFocusLost'; sonst undefiniert. */
  transient?: boolean;
}

/** Payload des 'audioFocusLost'-Events. */
export interface NativeAudioFocusLostDetail {
  /** true = nur kurzzeitig unterbrochen (Anruf/Klingelton), false = dauerhaft (z.B. anderer MediaPlayer). */
  transient: boolean;
}

/** Callback-Form fuer Remote-Commands ohne Payload. */
export type NativeRemoteCommandCallback = () => void;
/** Callback-Form fuer 'remoteSeek' (positionMs). */
export type NativeRemoteSeekCallback = (detail: NativeRemoteSeekDetail) => void;
/** Callback-Form fuer Audiofokus-Wechsel. */
export type NativeAudioFocusCallback = (detail: NativeAudioFocusLostDetail) => void;

/** Payload des 'remoteSeek'-Events (Millisekunden). */
export interface NativeRemoteSeekDetail {
  positionMs: number;
}

interface LiveCallNativePlugin {
  start(opts: LiveCallStartOptions): Promise<void>;
  stop(): Promise<void>;
  /** F3/P5: Audio-Modus im laufenden Betrieb wechseln. Optional — aelterer
   *  nativer Build ohne die Methode schluckt der Bruecken-Aufruf. */
  setMode?(opts: { mode: NativeAudioMode }): Promise<void>;
  /** F3/P5: Sperrbildschirm-/Notification-Infos setzen. Optional. */
  updateNowPlaying?(opts: NativeNowPlayingInfo): Promise<void>;
  /** F3/P5: Sperrbildschirm-/Notification-Infos entfernen. Optional. */
  clearNowPlaying?(): Promise<void>;
  // Capacitor liefert je nach Version ein Promise auf den Handle ODER den
  // Handle direkt — beide Formen werden in addNativeListener normalisiert.
  addListener(
    eventName: LiveCallEventName,
    cb: (event: NativeListenerPayload | undefined) => void,
  ): Promise<LiveCallListenerHandle> | LiveCallListenerHandle;
}

// ─── Plugin-Laden (SYNCHRON, statisch — kein dynamischer Import) ─────────────

// Proxy nach dem ersten registerPlugin cachen — wiederholtes Registrieren
// desselben Namens spammt sonst Capacitors "already registered"-Warnung bei
// jedem Aufruf (z.B. teardown() bei jedem Chat-Unmount).
let cachedPlugin: LiveCallNativePlugin | null = null;

/**
 * Holt den nativen LiveCall-Plugin-Proxy — vollständig synchron. Fehlt das
 * Plugin (Web/PWA/Alt-Shell vor dem Call-Modus-Store-Build) → null, und alle
 * exportierten Funktionen unten werden zu No-Ops.
 */
function loadLiveCallPlugin(): LiveCallNativePlugin | null {
  if (cachedPlugin) return cachedPlugin;
  try {
    if (Capacitor?.isPluginAvailable?.('LiveCall') !== true) return null;
    cachedPlugin = registerPlugin<LiveCallNativePlugin>('LiveCall');
    return cachedPlugin;
  } catch {
    return null;
  }
}

/**
 * Robustheits-Helfer: prueft, ob eine bestimmte native Methode auf dem
 * Plugin existiert. Aelterer Store-Build ohne die F3/P5-Erweiterungen
 * liefert sonst einen TypeError (Plugin-Methode undefined), was den Hook
 * beim Hörbuch-Start zerschiesst — wir wollen aber nur ein No-Op.
 */
function hasNativeMethod<K extends keyof LiveCallNativePlugin>(name: K): boolean {
  const plugin = loadLiveCallPlugin();
  if (!plugin) return false;
  const value = (plugin as unknown as Record<string, unknown>)[name as string];
  return typeof value === 'function';
}

// ─── Öffentliche API (alles no-op/null-safe ohne Plugin) ─────────────────────

/** Call-Modus starten (Foreground-Service/Notification) — best effort. */
let reviewAudioActive = false;
let reviewAudioGeneration = 0;
export function isNativeCallModeActive(): boolean { return reviewAudioActive; }

export async function startNativeCallMode(opts: LiveCallStartOptions): Promise<void> {
  reviewAudioActive = true;
  reviewAudioGeneration += 1;
  const plugin = loadLiveCallPlugin();
  if (!plugin) return;
  try {
    await plugin.start(opts);
  } catch (error) {
    // Der Call-Modus ist reine Komfort-Schicht — die Session läuft weiter.
    console.warn('[liveCall] start fehlgeschlagen (Session läuft ohne Call-Modus weiter):', error);
  }
}

/** Call-Modus beenden (Service/Notification abbauen) — best effort. */
export async function stopNativeCallMode(): Promise<void> {
  const generation = reviewAudioGeneration;
  const plugin = loadLiveCallPlugin();
  if (!plugin) { reviewAudioActive = false; return; }
  try {
    await plugin.stop();
    if (generation === reviewAudioGeneration) reviewAudioActive = false;
  } catch (error) {
    // Ohne bestaetigten Stopp bleibt die Bewertungssperre vorsichtshalber aktiv.
    console.warn('[liveCall] stop fehlgeschlagen:', error);
  }
}

/**
 * F3/P5: Audio-Modus im laufenden Betrieb wechseln — 'playback' (Hoerbuch)
 * ↔ 'call' (Entstummen). Die Sitzung / der Foreground-Service bleibt
 * unveraendert; nur Kategorie / Modus / Service-Typ werden atomar auf der
 * Main-Queue getauscht. Auf Alt-Shells ohne setMode() ist der Aufruf ein
 * No-Op (Fehler werden nur geloggt, niemals geworfen).
 */
export async function setNativeAudioMode(mode: NativeAudioMode): Promise<void> {
  const plugin = loadLiveCallPlugin();
  if (!plugin || !hasNativeMethod('setMode')) return;
  try {
    await plugin.setMode!({ mode });
  } catch (error) {
    console.warn('[liveCall] setMode fehlgeschlagen:', error);
  }
}

/**
 * F3/P5: Sperrbildschirm- und Medienbenachrichtigungs-Infos setzen
 * (Titel/Untertitel/Dauer/Position/Playing). No-Op auf Alt-Shells.
 */
export async function updateNativeNowPlaying(info: NativeNowPlayingInfo): Promise<void> {
  const plugin = loadLiveCallPlugin();
  if (!plugin || !hasNativeMethod('updateNowPlaying')) return;
  try {
    await plugin.updateNowPlaying!(info);
  } catch (error) {
    console.warn('[liveCall] updateNowPlaying fehlgeschlagen:', error);
  }
}

/**
 * F3/P5: Sperrbildschirm-/Notification-Infos entfernen. No-Op auf Alt-Shells.
 */
export async function clearNativeNowPlaying(): Promise<void> {
  const plugin = loadLiveCallPlugin();
  if (!plugin || !hasNativeMethod('clearNowPlaying')) return;
  try {
    await plugin.clearNowPlaying!();
  } catch (error) {
    console.warn('[liveCall] clearNowPlaying fehlgeschlagen:', error);
  }
}

/**
 * Android: Beenden-Knopf der Call-Notification → cb. Liefert IMMER einen
 * synchronen remove()-Handle (No-Op ohne Plugin) — der Aufrufer muss sich
 * nicht um die Promise-Form von Capacitors addListener kümmern; remove()
 * VOR der Promise-Auflösung wird nachgezogen.
 */
export function onNativeEndRequested(cb: () => void): LiveCallListenerHandle {
  return addNativeListener('endRequested', cb);
}

/**
 * iOS: AVAudioSession-Interruption (Anruf/Siri/Wecker) ist vorbei — das
 * Plugin hat die Session nativ reaktiviert, die Web-Schicht muss jetzt
 * Playback/Capture wieder anstoßen (WebKit resumed pausierte
 * srcObject-Elemente nicht zuverlässig selbst). Handle-Semantik wie
 * onNativeEndRequested.
 */
export function onNativeInterruptionEnded(cb: () => void): LiveCallListenerHandle {
  return addNativeListener('interruptionEnded', cb);
}

/**
 * F3/P5: Verdrahtet ALLE Remote-Commands (Sperrbildschirm /
 * Bluetooth-Steuerung / Android-Wearable) in EINEM Callback. Die App
 * entscheidet selbst, welche Aktion daraus wird (Play/Pause/Skip/Scrub).
 * Der Callback bekommt den Eventnamen — die Payload (positionMs) wird
 * separat als zweites Argument durchgereicht, sofern vorhanden.
 *
 *   'remotePlay' / 'remotePause' / 'remoteToggle' / 'remoteNext' /
 *   'remotePrevious' → position undefined
 *   'remoteSeek' → position = { positionMs: number }
 *
 * No-Op auf Alt-Shells ohne die Remote-Events (gibt einen Pass-Through-
 * Unsubscribe zurueck, der nichts abhaengt).
 */
export function onNativeRemoteCommand(
  cb: (event: 'remotePlay' | 'remotePause' | 'remoteToggle' | 'remoteNext' | 'remotePrevious', detail: undefined) => void,
  seekCb: (event: 'remoteSeek', detail: NativeRemoteSeekDetail) => void,
): LiveCallListenerHandle {
  // Drei separate Listener, deren Handles zu EINEM kombinierten Remove-
  // Handle zusammengefasst werden — so kann der Aufrufer EINEN Unsubscribe
  // aufrufen und alle drei gehen ab.
  const names = ['remotePlay', 'remotePause', 'remoteToggle', 'remoteNext', 'remotePrevious'] as const;
  const handles: LiveCallListenerHandle[] = [];
  for (const name of names) {
    handles.push(addNativeListener(name, (raw) => {
      try {
        cb(name, undefined);
      } catch (error) {
        console.warn(`[liveCall] remote-command listener (${name}) threw:`, error);
      }
      void raw;
    }));
  }
  const seekHandle = addNativeListener('remoteSeek', (raw) => {
    const positionMs = typeof raw?.positionMs === 'number' && Number.isFinite(raw.positionMs)
      ? raw.positionMs
      : 0;
    try {
      seekCb('remoteSeek', { positionMs });
    } catch (error) {
      console.warn('[liveCall] remote-seek listener threw:', error);
    }
    return positionMs;
  });
  handles.push(seekHandle);
  return {
    remove: () => {
      for (const handle of handles) {
        try {
          handle.remove();
        } catch {
          /* egal */
        }
      }
    },
  };
}

/**
 * F3/P5: Audiofokus-Wechsel abonnieren — Android liefert transient=true bei
 * kurzfristiger Unterbrechung (Klingelton), transient=false bei dauerhaftem
 * Verlust (anderer MediaPlayer). iOS liefert das passend uebersetzt.
 * No-Op auf Alt-Shells.
 */
export function onNativeAudioFocusChange(cb: NativeAudioFocusCallback): LiveCallListenerHandle {
  const lostHandle = addNativeListener('audioFocusLost', (raw) => {
    const transient = raw?.transient === true;
    try {
      cb({ transient });
    } catch (error) {
      console.warn('[liveCall] audioFocusLost listener threw:', error);
    }
  });
  const gainedHandle = addNativeListener('audioFocusGained', () => {
    try {
      cb({ transient: false });
    } catch (error) {
      console.warn('[liveCall] audioFocusGained listener threw:', error);
    }
  });
  return {
    remove: () => {
      try { lostHandle.remove(); } catch { /* egal */ }
      try { gainedHandle.remove(); } catch { /* egal */ }
    },
  };
}

// Gemeinsame addListener-Normalisierung für alle Events (Promise-Form von
// Capacitor ↔ synchroner Handle; remove() vor der Auflösung wird nachgezogen).
function addNativeListener(eventName: LiveCallEventName, cb: (event: NativeListenerPayload | undefined) => void): LiveCallListenerHandle {
  const plugin = loadLiveCallPlugin();
  if (!plugin) return { remove: () => undefined };
  let removed = false;
  let handle: LiveCallListenerHandle | null = null;
  try {
    const result = plugin.addListener(eventName, cb);
    if (result && typeof (result as Promise<LiveCallListenerHandle>).then === 'function') {
      void (result as Promise<LiveCallListenerHandle>)
        .then((resolved) => {
          handle = resolved;
          if (removed) {
            // remove() kam schon vor der Auflösung → sofort abhängen.
            try {
              resolved.remove();
            } catch {
              /* egal */
            }
          }
        })
        .catch((error) => {
          console.warn('[liveCall] addListener fehlgeschlagen:', error);
        });
    } else {
      handle = result as LiveCallListenerHandle;
    }
  } catch (error) {
    console.warn('[liveCall] addListener fehlgeschlagen:', error);
  }
  return {
    remove: () => {
      removed = true;
      try {
        handle?.remove();
      } catch {
        /* egal */
      }
    },
  };
}